import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import Quagga from 'quagga';
import { Card, Button, Form, Row, Col, Alert, Spinner, InputGroup, ToggleButtonGroup, ToggleButton, Image } from 'react-bootstrap'; // ListGroup, Badge, Modal removed
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCamera, faUpload, faLink, faKeyboard, faBarcode, faBrain, faTimes, faSync,
    faHistory, faPlusCircle, faArrowLeft, faCheckCircle // faSearch, faTrashAlt removed (handled by HistoryView)
} from '@fortawesome/free-solid-svg-icons';

import AnalysisResult from './AnalysisResult';
import BarcodeProductDisplay from './BarcodeProductDisplay';
import { callPollinationsTextApi } from '../services/aiService';
import { getScanHistory, hideUserActivity } from '../services/dbService';

// Import Reusable Components
import ViewModeLayout from './ui/ViewModeLayout'; // Adjust path as needed
import HistoryView from './ui/HistoryView';       // Adjust path as needed
import ItemDetailsModal from './ui/ItemDetailsModal'; // Adjust path as needed

// --- Constantes ---
const OPENFOODFACTS_API_BASE = 'https://world.openfoodfacts.org/api/v2/product/';
// const IMAGE_API_BASE_POLLINATIONS = 'https://image.pollinations.ai/prompt/'; // Not directly used if image generation is specific to details
// const DEFAULT_IMAGE_MODEL = 'flux';

const FoodScanner = ({
    userId,
    userHealthGoals = [],
    userRestrictions = [],
    onScanActivityLogged
}) => {
    const [viewMode, setViewMode] = useState('history');
    const [scanModeInternal, setScanModeInternal] = useState('barcode');
    const [imageUrl, setImageUrl] = useState('');
    const [barcode, setBarcodeState] = useState('');
    const [manualBarcode, setManualBarcode] = useState('');
    const [productData, setProductData] = useState(null);
    const [aiAnalysisData, setAiAnalysisData] = useState(null);
    const [errorBarcode, setErrorBarcode] = useState('');
    const [isLoadingBarcode, setIsLoadingBarcode] = useState(false);
    const [errorAI, setErrorAI] = useState('');
    const [isLoadingAI, setIsLoadingAI] = useState(false);
    const [isCameraActive, setIsCameraActive] = useState(false);
    const [inputError, setInputError] = useState('');
    const [uploadedImagePreview, setUploadedImagePreview] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = useRef(null);
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const quaggaInitialized = useRef(false);
    const detectionTimeoutRef = useRef(null);

    const [scanHistory, setScanHistory] = useState([]);
    const [isLoadingHistory, setIsLoadingHistory] = useState(false);
    const [historyError, setHistoryError] = useState('');
    const [selectedScanHistoryItem, setSelectedScanHistoryItem] = useState(null);
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    // historySearchTerm and hoveredScanId are managed by HistoryView

    const isLoading = isLoadingBarcode || isLoadingAI || isLoadingHistory;

    const fetchAndSetHistory = useCallback(async () => {
        if (userId) {
            setIsLoadingHistory(true); setHistoryError('');
            try {
                const history = await getScanHistory(userId);
                setScanHistory(history || []);
            } catch (err) {
                setHistoryError('Could not load scan history.');
            } finally { setIsLoadingHistory(false); }
        }
    }, [userId]);

    useEffect(() => { if (viewMode === 'history') { fetchAndSetHistory(); } }, [viewMode, fetchAndSetHistory]);

    const resetCommonInputStates = () => { setImageUrl(''); setUploadedImagePreview(null); setIsDragging(false); setInputError(''); if (fileInputRef.current) fileInputRef.current.value = ''; };
    const stopQuagga = useCallback(() => { if (quaggaInitialized.current) { try { Quagga.offDetected(null); Quagga.offProcessed(null); Quagga.stop(); } catch (e) { /* ignore */ } quaggaInitialized.current = false; } }, []);
    const stopCamera = useCallback(() => { if (videoRef.current?.srcObject) { videoRef.current.srcObject.getTracks().forEach(track => track.stop()); videoRef.current.srcObject = null; } setIsCameraActive(false); }, []);
    const stopAllCamerasAndQuagga = useCallback(() => { stopCamera(); stopQuagga(); clearTimeout(detectionTimeoutRef.current); }, [stopCamera, stopQuagga]);
    const resetScannerSpecificStates = () => {
        setBarcodeState(''); setManualBarcode(''); setProductData(null); setErrorBarcode(''); setIsLoadingBarcode(false);
        setAiAnalysisData(null); setErrorAI(''); setIsLoadingAI(false);
        clearTimeout(detectionTimeoutRef.current);
    };
    const handleScannerModeChange = (newMode) => {
        if (newMode === scanModeInternal || !newMode) return;
        stopAllCamerasAndQuagga(); resetCommonInputStates(); resetScannerSpecificStates();
        setScanModeInternal(newMode);
    };
    const handleClearScannerAndResults = () => {
        stopAllCamerasAndQuagga(); resetCommonInputStates(); resetScannerSpecificStates();
    };

    const processImageInput = async (source) => {
        handleClearScannerAndResults();
        setInputError(''); setIsLoadingAI(false); setIsLoadingBarcode(false);
        let dataUri; let previewSet = false;
        const tempLoadingSetter = scanModeInternal === 'foodImage' ? setIsLoadingAI : setIsLoadingBarcode;
        tempLoadingSetter(true);
        try {
            if (typeof source === 'string' && source.startsWith('http')) {
                setImageUrl(source); const proxyUrl = 'https://api.allorigins.win/raw?url='; const res = await fetch(proxyUrl + encodeURIComponent(source)); if (!res.ok) throw new Error(`Fetch failed: ${res.status}`); const blob = await res.blob(); if (!blob.type.startsWith('image/')) throw new Error('Not an image URL.'); dataUri = await new Promise((resolve, reject) => { const r = new FileReader(); r.onload = () => resolve(r.result); r.onerror = reject; r.readAsDataURL(blob); });
            } else if (source instanceof File || source instanceof Blob) {
                if (!source.type.startsWith('image/')) throw new Error('Invalid image file type.'); dataUri = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(source); });
            } else if (typeof source === 'string' && source.startsWith('data:image')) {
                dataUri = source;
            } else { throw new Error('Invalid image source.'); }
            setUploadedImagePreview(dataUri); previewSet = true; tempLoadingSetter(false);
            if (dataUri) {
                if (scanModeInternal === 'foodImage') { const base64 = dataUri.split(',')[1]; analyzeFoodImageWithAI(base64); }
                else if (scanModeInternal === 'barcode') { decodeBarcodeFromImage(dataUri); }
            }
        } catch (err) { setInputError(`Error processing input: ${err.message}`); tempLoadingSetter(false); if (!previewSet) setUploadedImagePreview(null); }
    };

    const handleManualBarcodeChange = (e) => setManualBarcode(e.target.value);
    const handleManualBarcodeSubmit = (e) => { e.preventDefault(); if (manualBarcode.trim()) { handleClearScannerAndResults(); setIsLoadingBarcode(true); setBarcodeState(manualBarcode.trim()); } };

    const decodeBarcodeFromImage = (dataUri) => {
        setErrorBarcode(''); setIsLoadingBarcode(true);
        Quagga.decodeSingle({ decoder: { readers: ["ean_reader", "upc_reader", "code_128_reader"] }, locate: true, src: dataUri, inputStream: { size: 800 }, numOfWorkers: navigator.hardwareConcurrency || 2 }, (result) => {
            setIsLoadingBarcode(false);
            if (result?.codeResult?.code) { setBarcodeState(result.codeResult.code); }
            else { setErrorBarcode('Barcode not detected in image.'); }
        });
    };

    const fetchProductData = useCallback(async (currentBarcodeToFetch) => {
        if (!currentBarcodeToFetch) return;
        setErrorBarcode(''); setProductData(null); setIsLoadingBarcode(true);
        try {
            const fields = 'product_name,brands,categories,image_front_url,nutriments,nutriscore_grade,ecoscore_grade,nova_group,quantity,ingredients_text,ingredients_analysis_tags,additives_tags';
            const response = await fetch(`${OPENFOODFACTS_API_BASE}${currentBarcodeToFetch}?fields=${fields}&lc=en`);
            if (response.ok) {
                const data = await response.json();
                if (data?.status === 1 && data.product) {
                    setProductData(data.product);
                    if (onScanActivityLogged && typeof onScanActivityLogged === 'function') {
                        const scanPayloadForDB = { scanBarcode: { result: data.product, code: currentBarcodeToFetch } };
                        await onScanActivityLogged('barcode_scan', scanPayloadForDB, true);
                        fetchAndSetHistory();
                    }
                } else { setErrorBarcode(data.status_verbose || `Product not found: ${currentBarcodeToFetch}.`); }
            } else { setErrorBarcode(`Fetch failed (${response.status}) for ${currentBarcodeToFetch}.`); }
        } catch (err) {
            setErrorBarcode(`Network error fetching ${currentBarcodeToFetch}.`);
        } finally { setIsLoadingBarcode(false); }
    }, [onScanActivityLogged, fetchAndSetHistory]);

    useEffect(() => { if (barcode) { fetchProductData(barcode); } else { setProductData(null); } }, [barcode, fetchProductData]);

    const handleTakePictureAndDecodeBarcode = () => {
        if (videoRef.current && canvasRef.current) {
            const C = canvasRef.current, V = videoRef.current; C.width = V.videoWidth; C.height = V.videoHeight; C.getContext('2d').drawImage(V, 0, 0, C.width, C.height); const dUrl = C.toDataURL('image/png');
            stopAllCamerasAndQuagga(); setUploadedImagePreview(dUrl); decodeBarcodeFromImage(dUrl);
        }
    };

    const analyzeFoodImageWithAI = useCallback(async (base64ImageData) => {
        setErrorAI(''); setAiAnalysisData(null); setIsLoadingAI(true);
        try {
            let goalContext = userHealthGoals.length > 0 ? 'User health goals:\n' + userHealthGoals.map(g => `- ${g.target}${g.deadline ? ` (Deadline: ${g.deadline})` : ''}`).join('\n') + '\nConsider these goals.\n\n' : '';
            let restrictionContext = userRestrictions.length > 0 ? 'User dietary restrictions:\n' + userRestrictions.map(r => `- ${typeof r === 'string' ? r : r.restriction}`).join('\n') + '\nRespect these restrictions.\n\n' : '';
            const systemPrompt = `
You are a world-class expert in nutrition, food safety, and diet optimization.
${goalContext}${restrictionContext}
You are helping a user analyze a food product based on a photo or ingredients label.
Perform a comprehensive evaluation considering not only health impacts but also sustainability and personalization.

Analyze the food and return the following insights in **valid JSON** format:

{
  "productName": string,
  "rating": number, // from 1 (worst) to 10 (best)
  "ratingExplanation": string,
  "ingredients": {
    "concerning": [
      {
        "name": string,
        "risk": "high" | "medium" | "low",
        "impact": string,
        "whyAvoid": string,
        "scientificEvidence": string
      }
    ],
    "safe": [string]
  },
  "insights": [
    {
      "category": string,
      "details": string,
      "recommendation": string,
      "evidence": string
    }
  ],
  "healthImplications": {
    "shortTerm": [string],
    "longTerm": [string]
  },
  "alternatives": [
    {
      "name": string,
      "benefits": string,
      "whereToFind": string
    }
  ],
  "nutritionEstimate": {
    "calories": string,
    "macronutrients": {
      "carbs": number,   // in grams,  per 100g
      "protein": number, // in grams
      "fat": number      // in grams
    },
    "sugar": string,
    "sodium": string,
    "fiber": string,
    "artificialContent": string,
    "preservatives": string,
    "transFat": string,
    "dailyValuePercentages": {
      "sugar": number,
      "sodium": number,
      "fat": number,
      "protein": number,
      "fiber": number
    }
  },
  "allergens": [string],
  "dietCompatibility": {
    "vegan": boolean,
    "keto": boolean,
    "lowCarb": boolean,
    "paleo": boolean,
    "glutenFree": boolean
  },
  "processingLevel": {
    "classification": "NOVA 1" | "NOVA 2" | "NOVA 3" | "NOVA 4",
    "description": string
  },
  "environmentalImpact": {
    "score": number, // 0 (very high impact) to 10 (minimal impact)
    "description": string
  }${userHealthGoals?.length > 0 ? `,
  "goalAlignment": [
    {
      "goalType": string,
      "alignment": "good" | "neutral" | "poor",
      "recommendation": string
    }
  ]` : ''}${userRestrictions?.length > 0 ? `,
  "restrictionFlags": [
    {
      "restrictedItem": string,
      "reason": string,
      "severity": "high" | "medium" | "low"
    }
  ]` : ''}
}
`;
            const userPrompt = 'Analyze this food image and provide detailed nutritional insights:';
            const result = await callPollinationsTextApi(systemPrompt, [{ type: 'text', text: userPrompt }, { type: 'image_url', image_url: { url: `data:image/jpeg;base64,${base64ImageData}` } }]);
            if (typeof result?.rating !== 'number') { throw new Error('Parsed AI result is missing expected fields.'); }
            setAiAnalysisData(result);
            if (onScanActivityLogged && typeof onScanActivityLogged === 'function') {
                const scanPayloadForDB = { scanImage: result };
                await onScanActivityLogged('image_scan', scanPayloadForDB, true);
                fetchAndSetHistory();
            }
        } catch (err) {
            setErrorAI(`AI analysis error: ${err.message}`);
        } finally { setIsLoadingAI(false); }
    }, [userHealthGoals, userRestrictions, onScanActivityLogged, fetchAndSetHistory]);


    const handleCaptureImageForAI = () => {
        if (videoRef.current && canvasRef.current) { const C = canvasRef.current, V = videoRef.current; C.width = V.videoWidth; C.height = V.videoHeight; C.getContext('2d').drawImage(V, 0, 0, C.width, C.height); const dUrl = C.toDataURL('image/jpeg', 0.9); stopAllCamerasAndQuagga(); processImageInput(dUrl); }
    };

    const startCamera = () => {
        handleClearScannerAndResults();
        stopAllCamerasAndQuagga(); // Detiene todo antes de empezar
        setIsCameraActive(true);
        setInputError('');
        setErrorBarcode('');

        const videoConstraints = {
            facingMode: "environment",
            width: { ideal: 1280 },
            height: { ideal: 720 }
        };

        navigator.mediaDevices.getUserMedia({ video: videoConstraints })
            .then(stream => {
                if (!videoRef.current) {
                    const errorMessage = "UI Error: Video element reference lost after camera stream was acquired. Please try again.";
                    setInputError(errorMessage);
                    console.warn(errorMessage);
                    stream.getTracks().forEach(track => track.stop()); // Liberar cámara
                    setIsCameraActive(false);
                    return;
                }
                videoRef.current.srcObject = stream;
                videoRef.current.onloadedmetadata = () => {
                    if (videoRef.current) {
                        videoRef.current.play().catch(e => {
                            const playErrorMessage = `Video Play Error: ${e.name} - ${e.message}. Camera might be busy or unresponsive.`;
                            setInputError(playErrorMessage); // Mostrar error en UI
                            console.error("Video play error:", e);
                            stopAllCamerasAndQuagga(); // Detener todo si el play falla
                        });
                    }
                };

                // Si el modo NO es barcode, aquí terminaría la configuración de la cámara
                // y estaría lista para que el "Food Analyzer" capture un frame.
                if (scanModeInternal !== 'barcode') {
                    // console.log("Camera started for non-barcode mode (e.g., Food Analyzer).");
                    // No se inicializa Quagga. La cámara está lista para captura manual.
                    return;
                }

                // --- Lógica específica para Barcode Scanner (QuaggaJS) ---
                if (typeof Quagga === 'undefined') {
                    const quaggaLibError = "Library Error: Barcode scanning library (QuaggaJS) not loaded. Refresh page or check internet.";
                    setErrorBarcode(quaggaLibError); // Mostrar en UI
                    console.error(quaggaLibError);
                    stopAllCamerasAndQuagga(); // Detener la cámara que ya se inició
                    return;
                }

                // Configuración de QuaggaJS (tomada de tu código original)
                const quaggaConfig = {
                    inputStream: {
                        name: "Live",
                        type: "LiveStream",
                        target: videoRef.current, // Importante: Quagga usa el elemento video
                        constraints: { // Constraints para Quagga, pueden ser diferentes a getUserMedia
                            width: 640,  // Reducir para Quagga puede mejorar rendimiento
                            height: 480,
                            facingMode: "environment"
                            // Quagga puede tener problemas si las constraints aquí son muy diferentes
                            // o incompatibles con el stream ya obtenido.
                            // A veces es mejor omitirlas si el stream ya está configurado,
                            // o asegurarse de que sean un subconjunto válido.
                        },
                        area: { // Área de detección
                            top: "25%", right: "10%",
                            left: "10%", bottom: "25%"
                        }
                    },
                    decoder: {
                        readers: ["ean_reader", "upc_reader", "code_128_reader"]
                    },
                    locate: true, // Intentar localizar el barcode en la imagen
                    frequency: 5, // Intentos de decodificación por segundo (ajustar según rendimiento)
                    numOfWorkers: navigator.hardwareConcurrency >= 4 ? 2 : 1, // No sobrecargar CPU en móviles
                };

                // console.log("Attempting to initialize Quagga with config:", quaggaConfig);

                Quagga.init(quaggaConfig, (err) => {
                    if (err) {
                        // Aquí es donde probablemente esté el problema si getUserMedia funciona.
                        let quaggaInitError = `Barcode Scanner Init Error: ${err.name || 'UnknownQuaggaError'} - ${err.message || 'No specific message.'}. `;
                        if (err.message && err.message.includes("getUserMedia")) {
                            quaggaInitError += "Quagga might be trying to re-access the camera with conflicting settings. Check Quagga's inputStream constraints.";
                        } else if (err.name === "OperationNotSupportedError") {
                            quaggaInitError += "Some camera features required by Quagga might not be supported by your browser/device.";
                        }
                        setErrorBarcode(quaggaInitError); // Mostrar en UI
                        console.error("Quagga.init() failed:", err);
                        stopAllCamerasAndQuagga(); // Detener todo
                        return;
                    }

                    // console.log("Quagga.init() successful. Starting scanner...");
                    quaggaInitialized.current = true;

                    Quagga.start();
                    // Nota: Quagga.start() no tiene un callback de error directo.
                    // Los errores después de start suelen venir por Quagga.onError o si el stream se corta.

                    const detector = (data) => {
                        if (data?.codeResult?.code) {
                            // console.log("Barcode detected by Quagga:", data.codeResult.code);
                            clearTimeout(detectionTimeoutRef.current);
                            Quagga.offDetected(detector); // Importante: desregistrar listener
                            stopAllCamerasAndQuagga(); // Detener cámara y Quagga
                            setBarcodeState(data.codeResult.code);
                        }
                    };
                    Quagga.onDetected(detector);

                    // Opcional: onProcessed para feedback visual (dibujar cajas)
                    Quagga.onProcessed(result => {
                        const drawingCtx = Quagga.canvas.ctx.overlay;
                        const drawingCanvas = Quagga.canvas.dom.overlay;
                        if (drawingCtx && drawingCanvas) { // Asegurarse que existen
                            drawingCtx.clearRect(0, 0, parseInt(drawingCanvas.getAttribute("width")), parseInt(drawingCanvas.getAttribute("height")));
                            if (result) {
                                if (result.boxes) {
                                    result.boxes.filter(box => box !== result.box).forEach(box => {
                                        Quagga.ImageDebug.drawPath(box, { x: 0, y: 1 }, drawingCtx, { color: "green", lineWidth: 2 });
                                    });
                                }
                                if (result.box) {
                                    Quagga.ImageDebug.drawPath(result.box, { x: 0, y: 1 }, drawingCtx, { color: "#00F", lineWidth: 2 });
                                }
                            }
                        }
                    });

                    Quagga.onError((quaggaErr) => {
                        // Errores durante el procesamiento de Quagga
                        // A veces puede ser un error interno de Quagga o un problema con el stream.
                        if (isCameraActive) { // Solo si la cámara debería estar activa
                            const quaggaRuntimeError = `Barcode Scanner Runtime Error: ${quaggaErr.name || 'QuaggaStreamError'} - ${quaggaErr.message || 'Issue during active scanning.'}`;
                            if (!errorBarcode) setErrorBarcode(quaggaRuntimeError); // No sobrescribir error de init si ya existe
                            console.error("Quagga.onError() caught:", quaggaErr);
                            // Considerar si se debe detener Quagga aquí:
                            // stopAllCamerasAndQuagga(); // Podría ser muy agresivo, depende del tipo de error
                        }
                    });
                });
            })
            .catch(err => {
                // Error de getUserMedia (acceso inicial a la cámara)
                let detailedErrorMessage = `Camera Access Error: ${err.name || 'UnknownError'}. Message: ${err.message || 'No specific message.'}`;
                switch (err.name) {
                    case 'NotFoundError': detailedErrorMessage = `No Camera Found (Error: ${err.name}). Ensure a rear-facing camera exists and is enabled. Requested W${videoConstraints.width.ideal}xH${videoConstraints.height.ideal}. Specific: ${err.message}`; break;
                    case 'NotAllowedError': detailedErrorMessage = `Camera Permission Denied (Error: ${err.name}). Allow camera access in browser settings. Specific: ${err.message}`; break;
                    case 'NotReadableError': detailedErrorMessage = `Camera In Use or Unreadable (Error: ${err.name}). Another app might be using it, or hardware issue. Specific: ${err.message}`; break;
                    case 'OverconstrainedError': detailedErrorMessage = `Camera Doesn't Support Settings (Error: ${err.name}). Requested: W${videoConstraints.width.ideal}xH${videoConstraints.height.ideal}, facingMode: ${videoConstraints.facingMode}. Failed constraint: ${err.constraint || 'N/A'}. Specific: ${err.message}`; break;
                    case 'SecurityError': detailedErrorMessage = `Security Error (Error: ${err.name}). Camera requires HTTPS (or localhost). Specific: ${err.message}`; break;
                    case 'AbortError': detailedErrorMessage = `Camera Access Aborted (Error: ${err.name}). Specific: ${err.message}`; break;
                    case 'TypeError': detailedErrorMessage = `Configuration Error (Error: ${err.name}). Invalid camera request. Specific: ${err.message}`; break;
                    default: detailedErrorMessage = `Unknown Camera Error (Name: ${err.name || 'N/A'}). Message: ${err.message || 'No specific message.'}`; break;
                }
                setInputError(detailedErrorMessage);
                console.error("getUserMedia Full Error Object:", err);
                setIsCameraActive(false);
                // stopAllCamerasAndQuagga(); // Ya se llama al inicio de startCamera, pero redundancia no daña.
            });
    };

    const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); if (!isDragging) setIsDragging(true); };
    const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); };
    const handleDrop = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); if (e.dataTransfer.files?.[0]) { processImageInput(e.dataTransfer.files[0]); e.dataTransfer.clearData(); } };
    const triggerFileInput = () => { if (fileInputRef.current) fileInputRef.current.click(); };

    // --- Navigation Handlers for ViewModeLayout ---
    const handleShowScannerView = () => { handleClearScannerAndResults(); resetCommonInputStates(); setViewMode('scanner'); };
    const handleShowHistoryView = () => { stopAllCamerasAndQuagga(); setViewMode('history'); };

    // --- History Item Actions (passed to HistoryView and ItemDetailsModal) ---
    const handleShowHistoryDetails = (scanItem) => { setSelectedScanHistoryItem(scanItem); setShowHistoryModal(true); };
    const handleCloseHistoryModal = () => { setSelectedScanHistoryItem(null); setShowHistoryModal(false); };
    const handleHideScanFromHistory = async (scanIdToHide, e) => {
        e.stopPropagation(); // Keep this if called from an event within HistoryView
        if (!scanIdToHide || !userId) { setHistoryError("Cannot hide scan: missing ID or user info."); return; }
        if (window.confirm("Are you sure you want to hide this scan from your history?")) {
            setIsLoadingHistory(true);
            try {
                const success = await hideUserActivity(scanIdToHide, userId);
                if (success) { setScanHistory(prev => prev.filter(item => item.id !== scanIdToHide)); setHistoryError(''); }
                else { setHistoryError("Failed to hide scan."); }
            } catch (err) {
                setHistoryError("An error occurred while hiding the scan.");
            } finally { setIsLoadingHistory(false); }
        }
    };

    // --- Content Rendering Functions for Reusable Components ---

    // Renders a summary of a scan item for the HistoryView list
    const renderScanHistoryItemSummary = (scan) => {
        const isBarcode = scan.activity_type === 'barcode_scan';
        // Accessing nested data based on your `getScanHistory` and `saveUserActivity` structure
        const resultData = scan.result; // This should contain { scanBarcode: ... } or { scanImage: ... }

        let title = "Scan";
        let subtitle = "Details unavailable";
        let icon = faCamera;
        let iconColor = "text-secondary";

        if (isBarcode && resultData?.scanBarcode) {
            const barcodeInfo = resultData.scanBarcode.result; // The actual product data
            const code = resultData.scanBarcode.code;
            title = barcodeInfo?.product_name || code || "Barcode Scan";
            subtitle = `Code: ${code || 'N/A'}`;
            icon = faBarcode;
            iconColor = "text-primary";
        } else if (scan.activity_type === 'image_scan' && resultData?.scanImage) {
            const aiInfo = resultData.scanImage; // The AI analysis object
            title = aiInfo?.productName?.slice(0, 40) || "AI Food Analysis";
            subtitle = aiInfo?.ratingExplanation?.substring(0, 70) + '...' || "Analyzed image";
            icon = faBrain;
            iconColor = "text-info";
        }

        return (
            <>
                <div className="fw-bold">
                    <FontAwesomeIcon icon={icon} className={`me-2 ${iconColor}`} />
                    {title}
                </div>
                <span className="text-muted small">{subtitle}</span>
            </>
        );
    };

    // Renders the full details of a scan (for modal or new scan display)
    const renderScanDetailsContent = (scanDataForDisplay) => {
        if (!scanDataForDisplay) return null;

        const isHistoryItem = !!scanDataForDisplay.activity_type;
        const type = isHistoryItem ? scanDataForDisplay.activity_type : scanDataForDisplay.type; // 'type' for new, 'activity_type' for history
        const timestamp = isHistoryItem ? scanDataForDisplay.created_at : null;

        // Determine the actual data object and code
        let displayData = null;
        let displayCode = null;
        let displayImageUrl = null;

        if (type === 'barcode_scan') {
            displayData = isHistoryItem ? scanDataForDisplay.result?.scanBarcode?.result : scanDataForDisplay.data;
            displayCode = isHistoryItem ? scanDataForDisplay.result?.scanBarcode?.code : scanDataForDisplay.code;
            if (displayData?.image_front_url) displayImageUrl = displayData.image_front_url;
        } else if (type === 'image_scan') {
            displayData = isHistoryItem ? scanDataForDisplay.result?.scanImage : scanDataForDisplay.data;
            if (!isHistoryItem && uploadedImagePreview) { // New AI scan uses live preview
                displayImageUrl = uploadedImagePreview;
            }
            // For history AI scan, if you stored an image URL within result.scanImage:
            // else if (isHistoryItem && displayData?.imageUrl) {
            //     displayImageUrl = displayData.imageUrl;
            // }
        }

        return (
            <Card className="mt-0 shadow-none bg-transparent border-0">
                {type === 'barcode_scan' && displayData && (
                    <>
                        {isHistoryItem && (<h4 className="text-center mb-3 mt-0 text-primary"><FontAwesomeIcon icon={faBarcode} /> Barcode Scan: {displayCode || 'N/A'} <small className="text-muted d-block">{timestamp ? new Date(timestamp).toLocaleString() : ''}</small></h4>)}
                        <BarcodeProductDisplay productData={displayData} />
                    </>
                )}
                {type === 'image_scan' && displayData && (
                    <>
                        {isHistoryItem && (<h4 className="text-center mb-3 mt-0 text-primary"><FontAwesomeIcon icon={faBrain} /> AI Analysis <small className="text-muted d-block">{timestamp ? new Date(timestamp).toLocaleString() : ''}</small></h4>)}
                        {displayImageUrl && ( /* Display image if available */
                            <Row className="justify-content-center mb-3">
                                <Col xs={12} md={isHistoryItem ? 8 : 10} lg={isHistoryItem ? 6 : 8}>
                                    <Image src={displayImageUrl} alt="Scanned/Analyzed food" fluid rounded className="shadow-sm border" />
                                </Col>
                            </Row>
                        )}
                        <AnalysisResult data={displayData} />
                    </>
                )}
                {!displayData && (<Alert variant="secondary" className="m-0">No detailed data to display for this scan.</Alert>)}
            </Card>
        );
    };

    // Renders the specific form/UI for the scanner itself
    const renderScannerForm = () => (
        <>
            <div className="text-center mb-4">
                <ToggleButtonGroup type="radio" name="internalScanMode" value={scanModeInternal} onChange={handleScannerModeChange} size="md" className="shadow-sm rounded-pill">
                    <ToggleButton id="scanner-mode-barcode" value="barcode" variant={scanModeInternal === 'barcode' ? 'primary' : 'outline-primary'} className="rounded-start-pill px-4 py-2"><FontAwesomeIcon icon={faBarcode} className="me-2" />Barcode</ToggleButton>
                    <ToggleButton id="scanner-mode-foodImage" value="foodImage" variant={scanModeInternal === 'foodImage' ? 'primary' : 'outline-primary'} className="rounded-end-pill px-4 py-2"><FontAwesomeIcon icon={faBrain} className="me-2" />AI Analyze</ToggleButton>
                </ToggleButtonGroup>
            </div>
            <Row className="mb-3 g-3 align-items-stretch">
                <Col xs={12} md={6}>
                    <div onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop} onClick={isLoading ? undefined : triggerFileInput}
                        style={{ border: `2px dashed ${isDragging ? 'var(--bs-primary)' : '#ccc'}`, borderRadius: '0.375rem', padding: '1rem', textAlign: 'center', cursor: isLoading ? 'default' : 'pointer', backgroundColor: isDragging ? '#e7f1ff' : '#f9f9f9', minHeight: '130px', display: 'flex', flexDirection: 'column', justifyContent: 'center', opacity: isLoading ? 0.7 : 1 }}
                        className={`hover-shadow ${isLoading ? 'pe-none' : ''}`}>
                        <input ref={fileInputRef} type="file" accept="image/*" onChange={(e) => e.target.files?.[0] && processImageInput(e.target.files[0])} style={{ display: 'none' }} disabled={isLoading} />
                        <FontAwesomeIcon icon={faUpload} size="2x" className="text-muted mb-2" />
                        <span className="text-secondary small">{isDragging ? "Drop image!" : `Drag & drop or click to upload ${scanModeInternal === 'barcode' ? 'barcode' : 'food'} image`}</span>
                    </div>
                    <div className="text-center mt-2">
                        <Button variant="outline-success" onClick={startCamera} size="lg" className="shadow-sm" disabled={isCameraActive || isLoading}><FontAwesomeIcon icon={faCamera} className="me-2" /> Use Camera</Button>
                    </div>
                </Col>
                <Col xs={12} md={6}>
                    <InputGroup className="mb-2 shadow-sm"> <InputGroup.Text><FontAwesomeIcon icon={faLink} /></InputGroup.Text> <Form.Control type="url" placeholder="Or paste image URL" value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} disabled={isLoading} /> <Button variant="secondary" onClick={() => imageUrl.trim() && processImageInput(imageUrl.trim())} disabled={!imageUrl.trim() || isLoading}>{isLoading ? <Spinner size="sm" /> : "Fetch"}</Button> </InputGroup>
                    {scanModeInternal === 'barcode' && (<Form onSubmit={handleManualBarcodeSubmit} className="d-flex shadow-sm mt-2"> <InputGroup> <InputGroup.Text><FontAwesomeIcon icon={faKeyboard} /></InputGroup.Text> <Form.Control type="text" value={manualBarcode} onChange={handleManualBarcodeChange} placeholder="Or type barcode" disabled={isLoadingBarcode} /> </InputGroup> <Button type="submit" variant="primary" className="ms-2" disabled={!manualBarcode.trim() || isLoadingBarcode}>{isLoadingBarcode ? <Spinner size="sm" /> : "Go"}</Button> </Form>)}
                </Col>
            </Row>
            {isCameraActive && (<div className="camera-container border p-3 rounded bg-light text-center shadow-sm mb-3 position-relative"> <video ref={videoRef} className="mb-2" style={{ width: '100%', maxWidth: '400px', maxHeight: '300px', border: '1px solid #ccc', borderRadius: '0.25rem' }} playsInline /> <canvas ref={canvasRef} style={{ display: 'none' }} /> <div className="d-flex justify-content-center gap-2 mt-2">{scanModeInternal === 'barcode' && <Button onClick={handleTakePictureAndDecodeBarcode} variant="outline-primary" disabled={isLoadingBarcode}><FontAwesomeIcon icon={faBarcode} /> Capture Barcode</Button>}{scanModeInternal === 'foodImage' && <Button onClick={handleCaptureImageForAI} variant="outline-success" disabled={isLoadingAI}><FontAwesomeIcon icon={faCamera} /> Capture Food Image</Button>}<Button onClick={stopAllCamerasAndQuagga} variant="outline-danger" disabled={isLoading}>Stop Camera</Button></div> </div>)}
            <div className="mt-3" style={{ minHeight: '50px' }}>
                {uploadedImagePreview && !aiAnalysisData && !productData && (<div className="mb-3 text-center p-3 bg-light border rounded shadow-sm position-relative"><h6 className="text-muted mb-2">Image Preview</h6><img src={uploadedImagePreview} alt="Preview" className="img-thumbnail" style={{ maxWidth: '300px', maxHeight: '250px' }} /></div>)}
                {(isLoadingBarcode || isLoadingAI) && <Alert variant="info" className="text-center py-2"><Spinner size="sm" className="me-2" /> {isLoadingAI ? "Analyzing Image..." : "Processing Barcode..."}</Alert>}
                {inputError && <Alert variant="warning" className="py-2">{inputError}</Alert>}
                {errorBarcode && scanModeInternal === 'barcode' && <Alert variant="danger" className="py-2">{errorBarcode}</Alert>}
                {errorAI && scanModeInternal === 'foodImage' && <Alert variant="danger" className="py-2">{errorAI}</Alert>}
            </div>

            {/* Display result of the current scan action */}
            {scanModeInternal === 'barcode' && productData && !isLoadingBarcode && renderScanDetailsContent({ type: 'barcode_scan', data: productData, code: barcode })}
            {scanModeInternal === 'foodImage' && aiAnalysisData && !isLoadingAI && renderScanDetailsContent({ type: 'image_scan', data: aiAnalysisData })}

            {(uploadedImagePreview || productData || aiAnalysisData || errorBarcode || errorAI || inputError || manualBarcode || imageUrl || isCameraActive) && (
                <Card.Footer className="text-center bg-light border-top mt-4">
                    <Button variant="outline-secondary" onClick={handleClearScannerAndResults} className="w-100">
                        <FontAwesomeIcon icon={faSync} className="me-2" /> Clear and Start New Scan/Analysis
                    </Button>
                </Card.Footer>
            )}
        </>
    );

    // Filter function specific to scans for HistoryView
    const scanHistoryFilter = (items, term) => {
        return items.filter(({ activity_type, result }) => {
            if (!result) return false; // Handle cases where result might be missing

            if (activity_type === 'barcode_scan') {
                // The 'result' for barcode_scan directly contains { data: productObject, code: 'barcodeNumber' }
                // if saved via onScanActivityLogged -> handleScanActivityAndLog -> handleLogActivity -> dbService.saveUserActivity
                // where `activityDataForDB` was `{ data: scanPayload.data, code: scanPayload.code }`
                const productName = result.data?.product_name?.toLowerCase() || '';
                const code = result.code?.toLowerCase() || '';
                return productName.includes(term) || code.includes(term);
            }
            if (activity_type === 'image_scan') {
                // The 'result' for image_scan directly contains the AI analysis object
                const productName = result.data?.productName?.toLowerCase() || ''; // Assuming AI result has productName
                const explanation = result.data?.ratingExplanation?.toLowerCase() || '';
                return productName.includes(term) || explanation.includes(term);
            }
            return false;
        });
    };

    return (
        <ViewModeLayout
            viewMode={viewMode}
            onShowGeneratorView={handleShowScannerView}
            onShowHistoryView={handleShowHistoryView}
            historyTitle="Scan History"
            generatorTitle="Food Scanner"
            historyIcon={faHistory}
            generatorIcon={faCamera}
            newItemButtonText="New Scan"
        >
            {viewMode === 'history' ? (
                <HistoryView
                    items={scanHistory}
                    isLoading={isLoadingHistory}
                    error={historyError}
                    onSelectItem={handleShowHistoryDetails}
                    onHideItem={handleHideScanFromHistory}
                    renderItemSummary={renderScanHistoryItemSummary}
                    searchPlaceholder="Search scan history..."
                    itemTypePlural="Scans"
                    filterFunction={scanHistoryFilter}
                />
            ) : (
                renderScannerForm()
            )}

            <ItemDetailsModal
                show={showHistoryModal}
                onHide={handleCloseHistoryModal}
                itemData={selectedScanHistoryItem}
                renderItemDetailsContent={renderScanDetailsContent}
                modalTitle={
                    selectedScanHistoryItem?.activity_type === 'barcode_scan'
                        ? "Barcode Scan Details"
                        : "AI Analysis Details"
                }
            />
            {/* FoodScanner specific styles */}
            <style jsx global>{`
                .camera-container video { border-radius: 0.375rem; }
                /* Add any other FoodScanner-specific styles here,
                   e.g., for specific layout adjustments in the scanner form */
                .bg-light-success { background-color: #d1e7dd !important; } /* If used by a save feedback */
            `}</style>
        </ViewModeLayout>
    );
};

export default FoodScanner;